import React, { useCallback, useEffect, useRef, useState } from 'react';
import { prop } from 'ramda';
import { useIsMounted } from 'js/hooks/useIsMounted';
import { useComponentTranslations } from 'js/hooks/component';
import { Autocomplete } from 'js/components/common/Fields/Autocomplete';
import { IAutocompleteOption } from 'js/components/common/Fields/Autocomplete/types';

import * as Types from '../../types';
import * as hooks from '../../hooks';
import * as helpers from '../../helpers';
import { SearchOption } from '../SearchOption';
import { useDynamicReducerSelector } from '../../../../store';
import { useFieldLabel } from '../../../ChangeFormField/hooks';

export const StreetNameLookup: React.FC<Types.IInnerLookupFieldConfig> = ({
    disabled,
    field,
    fieldId,
    isFieldHidden,
    className,
    classNames,
    retrievedAddress,
    onRetrieve,
    ChangeFormField
}) => {
    const isMounted = useIsMounted();
    const translate = useComponentTranslations();
    const searchStreetName = hooks.useSearchStreetName();
    const hiddenFields = useDynamicReducerSelector(prop('hiddenFields'));
    const searchText = useRef<string>('');
    const [error, setError] = useState('');

    const streetNameField = field.innerFields.streetName;
    const showInnerField = streetNameField && !hiddenFields.includes(streetNameField.name);
    const showAddressLookup = !showInnerField && (retrievedAddress?.city || retrievedAddress?.zip);

    const validate = hooks.useLookupValidate(streetNameField);
    const [label, getLabelText] = useFieldLabel(streetNameField?.label || '');

    const setAddressLookupError = useCallback((err: any) => {
        const defaultError = translate('playtech.mobile.system.form.addressLookupSwiss.serviceError.default');
        const message = translate(`playtech.mobile.system.form.addressLookupSwiss.serviceError.${err?.errorCode}`,
            err.errorMessage || defaultError
        );

        setError(message);
    }, [translate]);

    const onSearchChange = useCallback(async (value: string): Promise<IAutocompleteOption[] | void> => {
        setError('');
        searchText.current = value;

        try {
            const searchResults = await searchStreetName(value, retrievedAddress);

            return helpers.streetSearchResultsToOptions(searchResults, searchText.current);
        } catch (postCodeError: any) {
            isMounted() && setAddressLookupError(postCodeError);
        }
    }, [isMounted, retrievedAddress, searchStreetName, setAddressLookupError]);

    const onOptionSelect = useCallback(async (option: IAutocompleteOption): Promise<IAutocompleteOption[] | void> => {
        const selectedItem = option.data?.retrievedAddress as Types.IParsedAutoCompleteItem;

        setError('');
        searchText.current = '';

        onRetrieve(selectedItem);
    }, [onRetrieve]);

    useEffect(() => {
        if (!showAddressLookup) {
            setError('');
            searchText.current = '';
        }
    }, [showAddressLookup]);

    if (!streetNameField) {
        return null;
    }

    const { isLabelAsteriskEnabled, isPlaceholderAsteriskEnabled } = streetNameField.validationRules?.find(rule => rule.type === 'required') || {};
    const placeholder = streetNameField.placeholder && translate(streetNameField.placeholder, streetNameField.placeholder);

    return (
        <>
            {showAddressLookup && (
                <Autocomplete
                    aria-label={getLabelText() || placeholder || translate('playtech.mobile.system.input.aria-label')}
                    className={className}
                    classNames={{ container: classNames?.autocomplete }}
                    disabled={disabled}
                    error={error}
                    id={fieldId}
                    infoTooltip={
                        streetNameField.infoTooltip && translate(streetNameField.infoTooltip, streetNameField.infoTooltip)
                    }
                    isLabelAsteriskEnabled={isLabelAsteriskEnabled}
                    isPlaceholderAsteriskEnabled={isPlaceholderAsteriskEnabled}
                    label={label}
                    minSymbolsToSearch={2}
                    name={`${streetNameField.name}_lookup`}
                    noOptionsMessage={translate('playtech.mobile.system.form.addressLookupSwiss.serviceError.noResults')}
                    options={[]}
                    Option={SearchOption}
                    onSearchChange={onSearchChange}
                    onOptionSelect={onOptionSelect}
                    placeholder={placeholder}
                    validate={validate}
                />
            )}
            {showInnerField && (
                <ChangeFormField
                    field={streetNameField}
                    className={className}
                    isFieldHidden={isFieldHidden}
                    disabled={disabled || retrievedAddress?.streetName}
                />
            )}
        </>
    );
};
